/**
 * StructuredLogAdapter.ts - StructuredLogAdapter
 * 
 * CHANGE HISTORY:
 * - 251226_0856 [active] danielbeer: Remove @ts-nocheck and fix type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { LogLevel, LogEntry } from '../models/LogEntry.js';
import { type Actr } from '@opcentral/typr-lib';

/**
 * StructuredLogAdapter class
 */
interface StructuredLogOptions {
  minLevel?: number;
  endpoint?: string;
  batchSize?: number;
  flushInterval?: number;
  service?: string;
  environment?: string;
  [key: string]: any;
}

export class StructuredLogAdapter {
  minLevel: number;
  options: StructuredLogOptions;
  private buffer: LogEntry[] = [];
  private flushTimer: NodeJS.Timeout | null = null;

  constructor(options: StructuredLogOptions = {}) {
    this.minLevel = options.minLevel ?? LogLevel.INFO;
    this.options = {
      endpoint: options.endpoint, // Will be resolved via NVR if not provided
      batchSize: options.batchSize || 10,
      flushInterval: options.flushInterval || 5000, // 5 seconds
      service: options.service || 'opcentral',
      environment: options.environment || process.env.OC_ENV || 'dev',
      ...options
    };
    
    this.buffer = [];
    this.flushTimer = null;
    
    // Don't initialize NVR here to avoid circular dependency
    // NVR will be lazily initialized in getEndpoint() if needed
    
    // Start flush timer
    this.startFlushTimer();
  }
  
  /**
   * Write log entry
   */
  async write(logEntry: LogEntry): Promise<void> {
    if (logEntry.level < this.minLevel) {
      return;
    }
    
    // Add service and environment to entry
    const structuredLog = new LogEntry({
      id: logEntry.id,
      timestamp: typeof logEntry.timestamp === 'number' ? logEntry.timestamp : Date.now(),
      level: logEntry.level,
      message: logEntry.message,
      category: logEntry.category,
      userId: logEntry.userId || undefined,
      sessionId: logEntry.sessionId || undefined,
      context: logEntry.context,
      tags: logEntry.tags,
      metadata: logEntry.metadata,
      error: logEntry.error || undefined,
      stack: logEntry.stack || undefined,
      service: this.options.service,
      environment: this.options.environment
    });
    
    this.buffer.push(structuredLog);
    
    // Flush if buffer is full
    if (this.buffer.length >= (this.options.batchSize || 10)) {
      await this.flush();
    }
  }
  
  /**
   * Flush buffer to endpoint
   */
  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;
    
    // Skip if no endpoint provided - caller must provide explicit endpoint
    // Note: loggr-lib cannot use nvr-lib due to circular dependency
    if (!this.options.endpoint) {
      this.buffer = []; // Clear buffer
      return;
    }
    
    const logs = [...this.buffer];
    this.buffer = [];
    
    try {
      await fetch(this.options.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs })
      });
    } catch (error) {
      // Silently fail - don't spam console in dev environments
      // Re-add logs to buffer on failure
      this.buffer.unshift(...logs);
    }
  }
  
  
  /**
   * Start flush timer
   */
  startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.options.flushInterval || 5000);
  }
  
  /**
   * Stop flush timer
   */
  stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }
  
  /**
   * Close adapter
   */
  async close(): Promise<void> {
    this.stopFlushTimer();
    await this.flush();
  }
}

export default StructuredLogAdapter;
